import type {Configuration} from '../configuration';
import type {AxiosInstance, AxiosPromise, RawAxiosRequestConfig} from 'axios';
import {BASE_PATH, BaseAPI, operationServerMap, RequestArgs} from '../base';
import globalAxios from 'axios';
import {
  assertParamExists,
  createRequestFunction,
  DUMMY_BASE_URL,
  serializeDataIfNeeded,
  setSearchParams,
  toPathString,
} from '../common';
import {Organizations} from '../types/organization';

/**
 * OrgServiceApi - axios parameter creator
 * @export
 */
export const OrgServiceApiAxiosParamCreator = function (configuration?: Configuration) {
  return {
    /**
     * 查找用户所属组织
     * @summary 查找用户所属组织
     * @param {string} [userId]
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    orgServiceFind: async (
      userId?: string,
      options: RawAxiosRequestConfig = {}
    ): Promise<RequestArgs> => {
      const localVarPath = `/org/organizations`;
      // use dummy base URL string because the URL constructor only accepts absolute URLs.
      const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
      let baseOptions;
      if (configuration) {
        baseOptions = configuration.baseOptions;
      }

      const localVarRequestOptions = {method: 'GET', ...baseOptions, ...options};
      const localVarHeaderParameter = {} as any;
      const localVarQueryParameter = {} as any;

      if (userId !== undefined) {
        localVarQueryParameter['userId'] = userId;
      }

      setSearchParams(localVarUrlObj, localVarQueryParameter);
      let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
      localVarRequestOptions.headers = {
        ...localVarHeaderParameter,
        ...headersFromBaseOptions,
        ...options.headers,
      };

      return {
        url: toPathString(localVarUrlObj),
        options: localVarRequestOptions,
      };
    },

    /**
     * 更新组织信息
     * @summary 更新组织信息
     * @param {string} orgId
     * @param {OrgUpdateReq} orgUpdateReq
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    orgServiceUpdate: async (
      orgId: string,
      orgUpdateReq: OrgUpdateReq,
      options: RawAxiosRequestConfig = {}
    ): Promise<RequestArgs> => {
      // verify required parameter 'orgId' is not null or undefined
      assertParamExists('orgServiceUpdate', 'orgId', orgId);
      // verify required parameter 'orgUpdateReq' is not null or undefined
      assertParamExists('orgServiceUpdate', 'orgUpdateReq', orgUpdateReq);

      const localVarPath = `/org/update/{orgId}`.replace(
        `{${'orgId'}}`,
        encodeURIComponent(String(orgId))
      );

      // use dummy base URL string because the URL constructor only accepts absolute URLs.
      const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
      let baseOptions;
      if (configuration) {
        baseOptions = configuration.baseOptions;
      }

      const localVarRequestOptions = {method: 'PUT', ...baseOptions, ...options};
      const localVarHeaderParameter = {} as any;
      const localVarQueryParameter = {} as any;

      localVarHeaderParameter['Content-Type'] = 'application/json';

      setSearchParams(localVarUrlObj, localVarQueryParameter);
      let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
      localVarRequestOptions.headers = {
        ...localVarHeaderParameter,
        ...headersFromBaseOptions,
        ...options.headers,
      };
      localVarRequestOptions.data = serializeDataIfNeeded(
        orgUpdateReq,
        localVarRequestOptions,
        configuration
      );

      return {
        url: toPathString(localVarUrlObj),
        options: localVarRequestOptions,
      };
    },
  };
};

/**
 * OrgServiceApi - functional programming interface
 * @export
 */
export const OrgServiceApiFp = function (configuration?: Configuration) {
  const localVarAxiosParamCreator = OrgServiceApiAxiosParamCreator(configuration);
  return {
    /**
     * 查找用户所属组织
     * @summary 查找用户所属组织
     * @param {string} [userId]
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    async orgServiceFind(
      userId?: string,
      options?: RawAxiosRequestConfig
    ): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Organizations>> {
      const localVarAxiosArgs = await localVarAxiosParamCreator.orgServiceFind(userId, options);
      const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
      const localVarOperationServerBasePath =
        operationServerMap['OrgServiceApi.orgServiceFind']?.[localVarOperationServerIndex]?.url;
      return (axios, basePath) =>
        createRequestFunction(
          localVarAxiosArgs,
          globalAxios,
          BASE_PATH,
          configuration
        )(axios, localVarOperationServerBasePath || basePath);
    },

    /**
     * 更新组织信息
     * @summary 更新组织信息
     * @param {string} orgId
     * @param {OrgUpdateReq} orgUpdateReq
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    async orgServiceUpdate(
      orgId: string,
      orgUpdateReq: OrgUpdateReq,
      options?: RawAxiosRequestConfig
    ): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
      const localVarAxiosArgs = await localVarAxiosParamCreator.orgServiceUpdate(
        orgId,
        orgUpdateReq,
        options
      );
      const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
      const localVarOperationServerBasePath =
        operationServerMap['OrgServiceApi.orgServiceUpdate']?.[localVarOperationServerIndex]?.url;
      return (axios, basePath) =>
        createRequestFunction(
          localVarAxiosArgs,
          globalAxios,
          BASE_PATH,
          configuration
        )(axios, localVarOperationServerBasePath || basePath);
    },
  };
};

/**
 * OrgServiceApi - factory interface
 * @export
 */
export const OrgServiceApiFactory = function (
  configuration?: Configuration,
  basePath?: string,
  axios?: AxiosInstance
) {
  const localVarFp = OrgServiceApiFp(configuration);
  return {
    /**
     * 查找用户所属组织
     * @summary 查找用户所属组织
     * @param {string} [userId]
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    orgServiceFind(userId?: string, options?: any): AxiosPromise<Organizations> {
      return localVarFp.orgServiceFind(userId, options).then(request => request(axios, basePath));
    },

    /**
     * 更新组织信息
     * @summary 更新组织信息
     * @param {string} orgId
     * @param {OrgUpdateReq} orgUpdateReq
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    orgServiceUpdate(orgId: string, orgUpdateReq: OrgUpdateReq, options?: any): AxiosPromise<void> {
      return localVarFp
        .orgServiceUpdate(orgId, orgUpdateReq, options)
        .then(request => request(axios, basePath));
    },
  };
};

/**
 * OrgServiceApi - object-oriented interface
 * @export
 * @class OrgServiceApi
 * @extends {BaseAPI}
 */
export class OrgServiceApi extends BaseAPI {
  /**
   * 查找用户所属组织
   * @summary 查找用户所属组织
   * @param {string} [userId]
   * @param {*} [options] Override http request option.
   * @throws {RequiredError}
   * @memberof OrgServiceApi
   */
  public orgServiceFind(userId?: string, options?: RawAxiosRequestConfig) {
    return OrgServiceApiFp(this.configuration)
      .orgServiceFind(userId, options)
      .then(request => request(this.axios, this.basePath));
  }

  /**
   * 更新组织信息
   * @summary 更新组织信息
   * @param {string} orgId
   * @param {OrgUpdateReq} orgUpdateReq
   * @param {*} [options] Override http request option.
   * @throws {RequiredError}
   * @memberof OrgServiceApi
   */
  public orgServiceUpdate(
    orgId: string,
    orgUpdateReq: OrgUpdateReq,
    options?: RawAxiosRequestConfig
  ) {
    return OrgServiceApiFp(this.configuration)
      .orgServiceUpdate(orgId, orgUpdateReq, options)
      .then(request => request(this.axios, this.basePath));
  }
}

/**
 * Organization update request
 * @export
 * @interface OrgUpdateReq
 */
export interface OrgUpdateReq {
  /**
   * 组织名字
   * @type {string}
   * @memberof OrgUpdateReq
   */
  name?: string;
  /**
   * 描述信息
   * @type {string}
   * @memberof OrgUpdateReq
   */
  desc?: string;
}
